import React, { useEffect, useState } from 'react';

import { Notification } from '../../types';
import styles from './Notification.module.css';

interface NotificationToastProps {
  notification: Notification;
  onRemove: (id: number) => void;
}

const NotificationToast: React.FC<NotificationToastProps> = ({ notification, onRemove }) => {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(() => onRemove(notification.id), 500); // Match animation duration
    }, 4500); // Start exit animation before auto-removal

    return () => clearTimeout(timer);
  }, [notification.id, onRemove]);

  const handleRemove = () => {
    setExiting(true);
    setTimeout(() => onRemove(notification.id), 500);
  };

  // `notification_type` is the delivery channel (in_app/email/sms/push),
  // not the visual severity. Read `severity` for the toast styling.
  const severity = notification.severity ?? 'info';
  const typeClass = styles[severity] || styles.info;

  const iconGlyph: Record<NonNullable<Notification['severity']>, string> = {
    success: '✓',
    error: '!',
    warning: '!',
    info: 'i',
  };
  const glyph = iconGlyph[severity];

  const handleActionClick = () => {
    if (notification.action) {
      notification.action.onClick();
      handleRemove();
    }
  };

  return (
    <div
      className={`${styles.toast} ${typeClass} ${exiting ? styles.exiting : ''}`}
      role={notification.category === 'system' ? 'alert' : 'status'}
      aria-live={notification.category === 'system' ? 'assertive' : 'polite'}
      aria-atomic="true"
    >
      <div className={styles.icon} aria-hidden="true">
        {glyph}
      </div>
      <div className={styles.content}>
        <p>{notification.message}</p>
        {notification.action && (
          <button
            onClick={handleActionClick}
            className={styles.actionButton}
            aria-label={notification.action.label}
          >
            {notification.action.label}
          </button>
        )}
      </div>
      <button onClick={handleRemove} className={styles.closeButton} aria-label="Close notification">
        &times;
      </button>
    </div>
  );
};

export default NotificationToast;
